import { z } from 'zod';
import { LIMITS } from './limits.js';
/** The complete, closed set of aggregations. There is deliberately no
 *  expression language and no SQL: an agent can only pick from this list, so a
 *  report definition can always be statically validated against a schema. */
export const AGGREGATIONS = ['sum', 'count', 'average', 'ratio_of_sums'];
const columnRef = z.string().min(1).max(64);
export const MetricSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('sum'), columnId: columnRef }),
    /** Counts rows. With `columnId`, counts rows whose value is non-null. */
    z.object({ kind: z.literal('count'), columnId: columnRef.optional() }),
    z.object({ kind: z.literal('average'), columnId: columnRef }),
    /** sum(numerator) / sum(denominator) — computed as a ratio of totals, not as
     *  an average of per-row ratios. The two differ whenever row weights differ,
     *  and the ratio of totals is nearly always the intended figure. */
    z.object({
        kind: z.literal('ratio_of_sums'),
        numeratorColumnId: columnRef,
        denominatorColumnId: columnRef
    })
]);
/** Every column a metric reads. Used for validation and compatibility checks. */
export function metricColumnIds(metric) {
    switch (metric.kind) {
        case 'sum':
        case 'average':
            return [metric.columnId];
        case 'count':
            return metric.columnId ? [metric.columnId] : [];
        case 'ratio_of_sums':
            return [metric.numeratorColumnId, metric.denominatorColumnId];
    }
}
/** Aggregations that require a numeric column. `count` works on any type. */
export function metricRequiresNumeric(metric) {
    return metric.kind !== 'count';
}
export function describeMetric(metric) {
    switch (metric.kind) {
        case 'sum':
            return `Sum of ${metric.columnId}`;
        case 'count':
            return metric.columnId ? `Count of rows with ${metric.columnId}` : 'Row count';
        case 'average':
            return `Average of ${metric.columnId}`;
        case 'ratio_of_sums':
            return `Sum of ${metric.numeratorColumnId} divided by sum of ${metric.denominatorColumnId}`;
    }
}
// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------
/** Why a metric produced no number. Surfaced to the reader so an empty cell is
 *  never mistaken for a real zero. */
export const UNAVAILABLE_REASONS = [
    'no_rows',
    'no_values',
    'zero_denominator',
    'not_finite',
    'missing_column'
];
const UNAVAILABLE_TEXT = {
    no_rows: 'No data to show',
    no_values: 'No numeric values to total',
    zero_denominator: 'The total it divides by is zero',
    not_finite: 'The result is not a finite number',
    missing_column: 'A column this uses is missing from the data'
};
export function explainUnavailable(reason) {
    return UNAVAILABLE_TEXT[reason];
}
export const available = (value) => Number.isFinite(value) ? { available: true, value } : { available: false, reason: 'not_finite' };
export const unavailable = (reason) => ({ available: false, reason });
/** Coerces a cell to a number for aggregation. Returns null for anything that
 *  is not already a finite number — including numeric-looking strings, which
 *  are the supplier's problem to fix rather than ours to guess at. */
function numericCell(value) {
    if (typeof value !== 'number')
        return null;
    return Number.isFinite(value) ? value : null;
}
function sumColumn(rows, columnId) {
    let total = 0;
    let count = 0;
    for (const row of rows) {
        const n = numericCell(row[columnId]);
        if (n === null)
            continue;
        total += n;
        count += 1;
    }
    return { total, count };
}
/**
 * Evaluates a metric over rows.
 *
 * Missing-value policy, applied uniformly:
 *   - `null`/absent cells are skipped, not treated as zero.
 *   - If nothing was left to aggregate, the result is `unavailable`, never 0.
 *   - A zero denominator yields `zero_denominator`, never Infinity or NaN.
 */
export function evaluateMetric(metric, rows, knownColumnIds) {
    if (knownColumnIds) {
        for (const id of metricColumnIds(metric)) {
            if (!knownColumnIds.has(id))
                return unavailable('missing_column');
        }
    }
    switch (metric.kind) {
        case 'count': {
            const columnId = metric.columnId;
            if (columnId === undefined)
                return available(rows.length);
            let n = 0;
            for (const row of rows) {
                const raw = row[columnId];
                if (raw !== null && raw !== undefined)
                    n += 1;
            }
            return available(n);
        }
        case 'sum': {
            if (rows.length === 0)
                return unavailable('no_rows');
            const { total, count } = sumColumn(rows, metric.columnId);
            if (count === 0)
                return unavailable('no_values');
            return available(total);
        }
        case 'average': {
            if (rows.length === 0)
                return unavailable('no_rows');
            const { total, count } = sumColumn(rows, metric.columnId);
            if (count === 0)
                return unavailable('no_values');
            return available(total / count);
        }
        case 'ratio_of_sums': {
            if (rows.length === 0)
                return unavailable('no_rows');
            const numerator = sumColumn(rows, metric.numeratorColumnId);
            const denominator = sumColumn(rows, metric.denominatorColumnId);
            if (numerator.count === 0 || denominator.count === 0)
                return unavailable('no_values');
            if (denominator.total === 0)
                return unavailable('zero_denominator');
            return available(numerator.total / denominator.total);
        }
    }
}
/** Groups rows by a dimension column, preserving first-seen order so a chart's
 *  category order stays stable across refreshes. */
export function groupRows(rows, columnId) {
    const groups = new Map();
    for (const row of rows) {
        const raw = row[columnId];
        const key = raw === null || raw === undefined ? null : String(raw);
        let group = groups.get(key);
        if (!group) {
            group = { key, label: key ?? '(no value)', rows: [] };
            groups.set(key, group);
        }
        group.rows.push(row);
    }
    return [...groups.values()];
}
export const METRIC_LABEL_MAX = LIMITS.maxLabelChars;
//# sourceMappingURL=metrics.js.map